/* eslint-env browser */
import posthog from "posthog-js";
import { isNavigationFailure } from "vue-router";
import { getConsent, hasAnalyticsConsent, subscribeConsent } from "./consent";
import {
  applyCampaignConsent, bindCampaignAccount, campaignProperties, captureRouteCampaign,
  clearCampaign,
} from "./campaignAttribution";
import {
  maskReplayText, maskReplayAttribute, maskReplayNetworkRequest,
  redactSensitiveText, REPLAY_BLOCK_SELECTOR,
} from "./replayPrivacy";

let initialized = false;
let installed = false;
const LANDING_ID_PARAMETER = "ph_distinct_id";
const ANONYMOUS_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EXCEPTION_AUTOCAPTURE = {
  capture_unhandled_errors: true,
  capture_unhandled_rejections: true,
  capture_console_errors: false,
};
const EXPECTED_NAVIGATION = /^(Redirected when going from .* via a navigation guard\.|Avoided redundant navigation to current location:|Navigation (cancelled|aborted) from .* (with a new navigation|via a navigation guard)\.)/;

// Analytics must never interrupt navigation or authentication.
function safely(callback) {
  try {
    return callback();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("PostHog analytics failed:", error);
    return undefined;
  }
}

function routePath(route) {
  const matched = route ? route.matched : [];
  const record = matched[matched.length - 1];
  return record ? record.path || "/" : "/";
}

function sanitizeProperties(properties, path) {
  if (!properties) return properties;
  const result = { ...properties };
  Object.keys(result).forEach((key) => {
    if (
      key.startsWith("$initial_") || key.startsWith("$session_entry_")
      || key.startsWith("utm_") || key === "ph_keyword"
    ) {
      delete result[key];
    }
  });
  if (result.$current_url) result.$current_url = `${window.location.origin}${path}`;
  if (result.$pathname) result.$pathname = path;
  if (result.$referrer && result.$referrer !== "$direct") {
    try {
      result.$referrer = new URL(result.$referrer).origin;
    } catch (error) {
      delete result.$referrer;
    }
  }
  ["$set", "$set_once"].forEach((key) => {
    if (result[key]) result[key] = sanitizeProperties(result[key], path);
  });
  return result;
}

function sanitizeExceptionDetails(value) {
  if (typeof value === "string") {
    return redactSensitiveText(value).replace(/https?:\/\/[^\s"'<>]+/g, (address) => {
      try {
        const url = new URL(address);
        return `${url.origin}${url.pathname}`;
      } catch (error) {
        return "[redacted URL]";
      }
    });
  }
  if (Array.isArray(value)) return value.map(sanitizeExceptionDetails);
  if (!value || typeof value !== "object") return value;
  const result = {};
  Object.keys(value).forEach((key) => {
    // Keep stack locations; never attach local variables, source snippets or breadcrumbs.
    if (!["vars", "context_line", "pre_context", "post_context", "$exception_steps"].includes(key)) {
      result[key] = sanitizeExceptionDetails(value[key]);
    }
  });
  return result;
}

function sanitizeEvent(event, path) {
  if (!event) return event;
  if (event.event === "$exception") {
    if (!hasAnalyticsConsent()) return null;
    const exceptions = event.properties && event.properties.$exception_list;
    if (Array.isArray(exceptions) && exceptions.some((exception) => (
      exception.type === "NavigationDuplicated" || EXPECTED_NAVIGATION.test(exception.value || "")
    ))) return null;
  }
  // URL credentials can also occur in SDK-generated person attribution.
  const result = { ...event };
  ["properties", "$set", "$set_once"].forEach((key) => {
    if (result[key]) result[key] = sanitizeProperties(result[key], path);
  });
  // Event-scoped attribution only: never restore SDK person/session attribution
  // or arbitrary URL parameters. The tab's consented campaign is authoritative.
  if (hasAnalyticsConsent()) {
    result.properties = { ...result.properties, ...campaignProperties() };
  }
  if (event.event === "$exception") {
    result.properties = {
      ...sanitizeExceptionDetails(result.properties),
      platform: "connect",
      environment: process.env.NODE_ENV,
      ...(["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname)
        || process.env.NODE_ENV !== "production" ? { is_test: true } : {}),
    };
  }
  return result;
}

export function captureException(error, properties = {}) {
  if (!initialized || !hasAnalyticsConsent() || isNavigationFailure(error)) return false;
  return safely(() => Boolean(posthog.captureException(error, properties)));
}

export function installVueErrorTracking(Vue) {
  const { config } = Vue;
  const previousHandler = config.errorHandler;
  config.errorHandler = (error, vm, info) => {
    captureException(error, {
      error_source: "vue",
      component_name: vm && vm.$options && vm.$options.name,
      vue_error_info: info,
    });
    if (previousHandler) previousHandler.call(Vue, error, vm, info);
    else {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };
}

export function captureEvent(event, properties = {}, options) {
  if (initialized && hasAnalyticsConsent()) {
    return safely(() => {
      if (options) posthog.capture(event, properties, options);
      else posthog.capture(event, properties);
      return true;
    });
  }
  return false;
}

export function isReplayRouteAllowed(route) {
  if (!route || !route.matched || !route.matched.length) return false;
  if (/(terminal|remote-support)/i.test(route.path || "")) return false;
  if (/^\/(login|billing|profile|developer|registry|masa)(\/|$)/i.test(route.path || "")) {
    return false;
  }
  return !route.matched.some((record) => (
    record.meta && record.meta.requiredPermissions
    && record.meta.requiredPermissions.includes("backoffice")
  ));
}

export function initPostHog(router, store) {
  const key = process.env.VUE_APP_POSTHOG_KEY;
  const enabled = process.env.VUE_APP_POSTHOG_ENABLED;
  if (
    installed
    || !key
    || enabled === "false"
    || (enabled !== "true" && process.env.NODE_ENV !== "production")
  ) {
    return;
  }
  installed = true;
  let authenticatedId = null;
  let landingId = null;

  const identifyUser = (user) => {
    if (!initialized || !hasAnalyticsConsent() || !user || !user.id) return;
    safely(() => {
      const id = String(user.id);
      const previousId = posthog.get_property("$user_id");
      if (previousId && previousId !== id) {
        clearCampaign();
        posthog.reset();
      }
      bindCampaignAccount(id);
      posthog.identify(id);
      if (landingId) {
        // The landing ID is anonymous; the authenticated account remains canonical.
        const result = landingId === id ? -1 : safely(() => posthog.alias(landingId, id));
        if (result != null && result !== -2) landingId = null;
      }
      if (authenticatedId !== id && captureEvent("connect_authenticated")) {
        authenticatedId = id;
      }
    });
  };

  const pageview = (to) => {
    const record = to.matched[to.matched.length - 1];
    if (!record) return;
    const path = routePath(to);
    captureEvent("$pageview", {
      $current_url: `${window.location.origin}${path}`,
      $pathname: path,
      route_name: to.name,
    });
  };

  const updateRecording = () => {
    if (!initialized) return;
    safely(() => {
      if (getConsent().sessionReplay && isReplayRouteAllowed(router.currentRoute)) {
        posthog.startSessionRecording();
      } else {
        posthog.stopSessionRecording();
      }
    });
  };

  const applyConsent = (next, previous, metadata) => {
    applyCampaignConsent(next, previous, metadata);
    bindCampaignAccount(store.getters.customer.id);
    if (!next.analytics) authenticatedId = null;
    if (next.decided && !next.analytics) landingId = null;
    if (!next.analytics && !next.sessionReplay) {
      if (initialized) {
        safely(() => posthog.set_config({
          capture_exceptions: false,
          capture_performance: { network_timing: false },
        }));
        safely(() => posthog.stopSessionRecording());
        safely(() => posthog.opt_out_capturing());
      }
      return;
    }

    if (!initialized) {
      safely(() => {
        posthog.init(key, {
          api_host: process.env.VUE_APP_POSTHOG_HOST || "https://eu.i.posthog.com",
          person_profiles: "identified_only",
          capture_pageview: false,
          capture_pageleave: false,
          autocapture: false,
          rageclick: false,
          capture_dead_clicks: false,
          capture_exceptions: false,
          capture_performance: { network_timing: false },
          disable_session_recording: true,
          opt_out_capturing_by_default: true,
          opt_out_persistence_by_default: true,
          enable_recording_console_log: false,
          session_recording: {
            maskAllInputs: true,
            // Apply selective redaction to every text node, including stray emails.
            maskTextSelector: "*",
            maskTextFn: maskReplayText,
            maskAllElementAttributes: false,
            maskAttributeFn: maskReplayAttribute,
            blockSelector: REPLAY_BLOCK_SELECTOR,
            recordHeaders: false,
            recordBody: false,
            recordCanvas: false,
            recordCrossOriginIframes: false,
            maskCapturedNetworkRequestFn: (request) => maskReplayNetworkRequest(request, {
              pagePath: routePath(router.currentRoute),
              apiRoot: process.env.VUE_APP_API_ROOT,
              routeAllowed: isReplayRouteAllowed(router.currentRoute),
              replayAllowed: getConsent().sessionReplay,
            }),
          },
          disable_surveys: true,
          save_campaign_params: false,
          disable_capture_url_hashes: true,
          before_send: (event) => sanitizeEvent(event, routePath(router.currentRoute)),
        });
        // The SDK skips localhost network capture unless this development hook is set.
        // Set it before the lazy recorder is created; consent still controls recording.
        if (
          ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname)
          && posthog.sessionRecording
        ) {
          // eslint-disable-next-line no-underscore-dangle
          posthog.sessionRecording._forceAllowLocalhostNetworkCapture = true;
        }
        initialized = true;
      });
    }
    if (!initialized) return;
    safely(() => {
      posthog.stopSessionRecording();
      if (!next.analytics || (previous && previous.analytics !== next.analytics)) {
        posthog.reset(true);
      }
      posthog.set_config({
        person_profiles: next.analytics ? "identified_only" : "never",
        capture_exceptions: next.analytics ? EXCEPTION_AUTOCAPTURE : false,
        capture_performance: { network_timing: next.sessionReplay },
      });
      posthog.opt_in_capturing({ captureEventName: false });
    });
    identifyUser(store.getters.customer);
    if (next.analytics && (!previous || !previous.analytics)) pageview(router.currentRoute);
    updateRecording();
  };

  store.subscribe((mutation) => {
    if (mutation.type === "AUTH_SUCCESS") identifyUser(mutation.payload);
    if (mutation.type === "AUTH_LOGOUT") {
      landingId = null;
      clearCampaign();
    }
    if (mutation.type === "AUTH_LOGOUT" && initialized && hasAnalyticsConsent()) {
      authenticatedId = null;
      safely(() => posthog.reset());
    }
  });
  router.beforeEach((to, from, next) => {
    captureRouteCampaign(to);
    bindCampaignAccount(store.getters.customer.id);
    if (initialized) safely(() => posthog.stopSessionRecording());
    if (
      to.matched.some((record) => record.meta && record.meta.requiresAuth)
      && Object.prototype.hasOwnProperty.call(to.query, LANDING_ID_PARAMETER)
    ) {
      const value = to.query[LANDING_ID_PARAMETER];
      const consent = getConsent();
      if (typeof value === "string" && ANONYMOUS_ID.test(value) && (!consent.decided || consent.analytics)) {
        landingId = value;
        identifyUser(store.getters.customer);
      }
      const query = { ...to.query };
      delete query[LANDING_ID_PARAMETER];
      next({
        path: to.path, query, hash: to.hash, replace: true,
      });
      return;
    }
    next();
  });

  router.afterEach((to) => {
    pageview(to);
    // Wait until App's blocking attribute and the new view are rendered.
    if (router.app) router.app.$nextTick(updateRecording);
    else updateRecording();
  });
  subscribeConsent(applyConsent);
  applyConsent(getConsent());
}
